import cnBind from "classnames/bind";
import type { StaticImageData } from "next/image";
import type { GetCategoryDto } from "@/entities/types/entities";
import { Routes } from "@/shared/constants";
import { CustomImage } from "@/shared/ui/CustomImage";
import { Card } from "@/view/Building/components/Card";
import { advantagesRegistry } from "@/data/advantages";
import dynamic from "next/dynamic";

import { Swiper, SwiperSlide } from "swiper/react";
import { Scrollbar } from "swiper/modules";

import "swiper/css";
import "swiper/css/scrollbar";

const PromotionBlock = dynamic(
    () => import("@/view/Promotion").then((mod) => mod.PromotionBlock),
);
const MapBlock = dynamic(
    () => import("@/view/MapBlock/MapBlock").then((mod) => mod.MapBlock),
    { ssr: false }
);
const FeedBackSlider = dynamic(
    () => import("@/view/Main/component/FeedBackSlider").then((mod) => mod.FeedBackSlider),
    { ssr: false }
);
const PartnersSlider = dynamic(
    () => import("@/view/Main/component/PartnersSlider/PartnersSlider").then((mod) => mod.PartnersSlider),
    { ssr: false }
);
const CaseBlock = dynamic(
    () => import("@/view/Main/component/CaseBlock").then((mod) => mod.CaseBlock),
);
const FormFeedback = dynamic(
    () => import("@/components/_Forms/FormFeedback").then((mod) => mod.FormFeedback),
    { ssr: false}
);
const FAQBlock = dynamic(
    () => import("@/view/FAQ").then((mod) => mod.FAQBlock),
);

import styles from "./Building.module.scss";
import { BlockSkeleton } from "@/components/BlockSkeleton";
import { Advantages } from "@/view/Advantages";
import { TextExpandable } from "@/view/Building/components/TextExpandable";
import { LazyBlock } from "@/components/LazyBlock";
import { advertisingRegistry } from "@/data/advertising";

const cx = cnBind.bind(styles);

type Props = {
    title?: string;
    description?: string;
    src: StaticImageData;
    alt: string;
    listCategory: GetCategoryDto[];
    url?: string;
    sectionId?: string;
    sectionNumber?: number;
};

export const BuildingPage = ({
                                 description,
                                 title,
                                 src,
                                 alt,
                                 listCategory,
                                 url = Routes.BUILDING,
                                 sectionId,
                                 sectionNumber,
                             }: Props) => {
    const adv = advertisingRegistry[url.slice(1)];

    return (
        <div className={cx("building")}>
            <div className={cx("main-block")}>
                <CustomImage
                    src={src}
                    alt={alt}
                    priority
                    fill
                    sizes="(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1440px) 1024px, 1600px"
                    className={cx("main-image")}
                />
                <div className={cx("wrapper", "container")}>
                    <div className={cx("content")}>
                        <h1>{title}</h1>
                        <span>{description}</span>
                    </div>
                </div>
            </div>

            {listCategory.length ? (
                <div className={cx("cards-container")}>
                    <div className={cx("cards-wrapper", "container")}>
                        <h2>Варианты размещения рекламы</h2>
                        <Swiper
                            modules={[Scrollbar]}
                            scrollbar={{ draggable: true }}
                            className={cx("cards")}
                            slidesPerView={"auto"}
                            spaceBetween={16}
                            breakpoints={{
                                1201: {
                                    slidesPerView: 4,
                                    spaceBetween: 24,
                                    allowTouchMove: listCategory.length > 4,
                                },
                                769: {
                                    slidesPerView: "auto",
                                    spaceBetween: 24,
                                    allowTouchMove: true,
                                }
                            }}
                        >
                            {listCategory.map((el, index) => (
                                <SwiperSlide key={index}>
                                    <Card
                                        className={cx(listCategory.length === 1 && "active")}
                                        href={{ pathname: `${url}/[slug]`, query: { slug: el?.urlTitle, id: el.id } }}
                                        as={`${url}/${el?.urlTitle}`}
                                        data={el}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            ) : null}

            <PromotionBlock sectionId={sectionId} sectionNumber={sectionNumber} />

            <LazyBlock fallback={<BlockSkeleton height="900px" />}>
                <MapBlock sectionId={sectionId}/>
            </LazyBlock>

            <Advantages data={advantagesRegistry[url.slice(1)]} />

            <LazyBlock fallback={<BlockSkeleton height="800px" />}>
                <FeedBackSlider />
            </LazyBlock>

            <LazyBlock fallback={<BlockSkeleton height="400px" />}>
                <PartnersSlider />
            </LazyBlock>

            <CaseBlock sectionId={sectionId} sectionNumber={sectionNumber} />

            <TextExpandable adv={adv} />

            <FAQBlock sectionId={sectionId} sectionNumber={sectionNumber} />

            <LazyBlock fallback={<BlockSkeleton height="500px" />}>
                <FormFeedback />
            </LazyBlock>
        </div>
    );
};
